import { useState } from 'react';
import {
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Badge,
  Button,
  Card,
  Descriptions,
  Typography,
  Radio,
  message,
  Tooltip,
} from 'antd';
import { Field, Form } from 'react-final-form';
import { usStates } from '../../../../../../Utils/data';
import moment from 'moment';
import dayjs from 'dayjs';
import { useVehicleSearch } from '../../../../../../Hooks/TT_ELD/Logs';
import { useUserData } from '../../../../../../Hooks/TT_ELD/Users';
import { useParams } from 'react-router-dom';
import { users } from '../../../../../../API/TT_ELD/Users/user';
import { sendEvent } from '../../../../../../Utils/analytics';
import { useSelector } from 'react-redux';
import _debounce from 'lodash/debounce';
import { useAssignLogBook } from '../../../../../../Hooks/TT_ELD/LogBook';
import { logbook } from '../../../../../../API/TT_ELD/logbook';
import type { DescriptionsProps } from 'antd';
import { useCompaniesData } from '../../../../../../Hooks/TT_ELD/Companies';
import debounce from 'lodash/debounce';
import { getUserFromLocalStorage } from '../../../../../../Utils/sidebar';
import { isWhiteLabelAdmin } from '../../../../../../Utils/whiteLabel';
import { CopyOutlined } from '@ant-design/icons';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

type MyObjectType = {
  [key: string | number]: any; // Индексная подпись с параметром типа 'string'
};
const { Option } = Select;
const { Title, Text } = Typography;

interface Params {
  [key: string]: string | undefined;
  id: string | undefined;
}

type Data = {
  data: any;
  isLoading?: boolean;
  refetch(): void;
};

const MainFields = () => {
  const { id } = useParams<Params>();
  const { data, isLoading, refetch }: Data = useUserData(id);

  const user = getUserFromLocalStorage();

  const isWhiteLabel = isWhiteLabelAdmin();

  const copyToClipboard = async (value: string | number | null | undefined) => {
    if (value === undefined || value === null || value === '') {
      message.warning('Public ID is empty');
      return;
    }

    const text = String(value);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success('Public ID copied');
    } catch {
      message.error('Failed to copy Public ID');
    }
  };

  const items: DescriptionsProps['items'] = [
    {
      label: 'ID',
      children: data?.id,
    },
    ...(data?.role !== 'dispatcher'
      ? [
          {
            label: 'Public ID',
            children: (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>{data?.publicId || '-'}</span>
                <Tooltip title="Copy Public ID">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(data?.publicId)}
                    disabled={!data?.publicId}
                  />
                </Tooltip>
              </span>
            ),
          },
          {
            label: 'Company',
            children: data?.company?.name,
          },
          {
            label: 'Subscription Type',
            children: data?.contract?.contractId
              ? `#${data?.contract?.contractId}`
              : 'Non-Contract',
          },
        ]
      : []),
    {
      label: 'Role',
      children: data?.role.toUpperCase(),
    },
    {
      label: 'Refresh date',
      children: data?.refresh_date ? moment(data?.refresh_date).format('YYYY-MM-DD HH:mm') : '',
    },
    {
      label: 'Created at',
      children: moment(data?.createdAt).format('YYYY-MM-DD HH:mm'),
    },
  ];
  const [logId, setLogId] = useState<undefined | number | string>('');

  const assignLogBook: any = useAssignLogBook(logId);
  const onSearchDebouncedLogBook = _debounce((searchValue) => {
    setLogId(searchValue);
  }, 1000);

  const handleButtonClick = () => {
    enterLoading(0);
    sendEvent('Users', 'User changed', 'MainFields Click Submit', 1);
  };
  const defaultValue: any = dayjs();
  const vehicleData: any = useVehicleSearch('', data?.companyId);
  const roleList = [
    {
      label: 'Accepted',
      value: 'accepted',
    },
    {
      label: 'Rejected ',
      value: 'rejected',
    },
  ];
  const [loadings, setLoadings] = useState<boolean[]>([]);
  const enterLoading = (index: number) => {
    setLoadings((prevLoadings) => {
      const newLoadings = [...prevLoadings];
      newLoadings[index] = true;
      return newLoadings;
    });

    setTimeout(() => {
      setLoadings((prevLoadings) => {
        const newLoadings = [...prevLoadings];
        newLoadings[index] = false;
        return newLoadings;
      });
    }, 6000);
  };

  const onSubmit = async (values: any) => {
    let body: MyObjectType = {};
    let key: string | number;
    let key2: string | number;
    for (key in values) {
      if (typeof data[key] === 'object' && !Array.isArray(data[key]) && data[key] != null) {
        for (key2 in values[key]) {
          if (data[key][key2] != values[key][key2]) {
            body[key] = values[key];
            break;
          }
        }
      } else if (data[key] != values[key]) {
        body[key] = values[key];
      }
    }
    if (body?.logbookId) {
      const logbookData = {
        logbookId: body.logbookId,
        userId: Number(id),
      };
      await logbook.assignLogbook(logbookData);
    }
    if ('config.is_full_accessed_support' in body) {
      body.config = {
        ...(body.config || {}),
        is_full_accessed_support: body.is_full_accessed_support,
      };

      delete body.is_full_accessed_support;
    }

    if (Object.keys(body).length > 0) {
      await users.userDataPatch(id, body);
    }

    refetch();
  };

  return (
    <Card loading={isLoading}>
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...data }}
        render={({ handleSubmit, values, submitting }) => (
          <form onSubmit={handleSubmit}>
            <div>
              <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                {!(isWhiteLabel && data?.role === 'dispatcher') && (
                  <Descriptions
                    title=""
                    bordered
                    column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 4, xxl: 5 }}
                    items={items}
                  />
                )}
                <Row gutter={[16, 10]}>
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <div>
                      <label>First Name: </label>
                      <Field
                        name="first_name"
                        render={({ input }: { input: any }) => (
                          <Input placeholder="First Name" type="text" {...input} />
                        )}
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <div>
                      <label>Second Name: </label>
                      <Field
                        name="second_name"
                        render={({ input }: { input: any }) => (
                          <Input placeholder="Second Name" type="text" {...input} />
                        )}
                      />
                    </div>
                  </Col>

                  {data.role !== 'dispatcher' && (
                    <Col xs={24} sm={24} md={12} lg={6}>
                      <div>
                        <label>Username: </label>
                        <Field
                          name="username"
                          render={({ input }: { input: any }) => (
                            <Input placeholder="Second Name" type="text" {...input} />
                          )}
                        />
                      </div>
                    </Col>
                  )}
                </Row>
                <Row gutter={[16, 10]}>
                  {data.role !== 'dispatcher' && (
                    <Col xs={24} sm={24} md={12} lg={6}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label>Vehicle: </label>
                        <Field
                          name="vehicleId"
                          render={({ input }: { input: any }) => (
                            <Select
                              defaultValue={data?.vehicle?.truck_number || ''}
                              showSearch
                              placeholder="Select a Vehicle"
                              optionFilterProp="children"
                              onChange={(value, option) => {
                                input.onChange(value);
                              }}
                              filterOption={(input, option: any) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              options={vehicleData?.data?.data?.map((option: any) => ({
                                value: option.id,
                                label: option.truck_number,
                              }))}
                            />
                          )}
                        />
                      </div>
                    </Col>
                  )}

                  {data.role !== 'dispatcher' && (
                    <Col xs={24} sm={24} md={12} lg={6}>
                      <div>
                        <label>License Number: </label>
                        <Field
                          name="license_number"
                          render={({ input }: { input: any }) => (
                            <Input placeholder="Second Name" type="text" {...input} />
                          )}
                        />
                      </div>
                    </Col>
                  )}

                  {data.role !== 'dispatcher' && (
                    <Col xs={24} sm={24} md={12} lg={6}>
                      <div>
                        <label>License State:</label>
                        <br />
                        <Field
                          name="license_state"
                          render={({ input }: { input: any }) => (
                            <Select
                              defaultValue={input.value}
                              onChange={(value, option) => {
                                input.onChange(value);
                              }}
                              style={{ width: '100%' }}
                            >
                              {usStates.map((u) => (
                                <Option key={u.id}>{u.name}</Option>
                              ))}
                            </Select>
                          )}
                        />
                      </div>
                    </Col>
                  )}

                  {data.role === 'dispatcher' && (
                    <Col xs={24} sm={24} md={12} lg={6}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label> Logbook </label>
                        <Field
                          name="logbookId"
                          render={({ input }: { input: any }) => (
                            <Select
                              defaultValue={data?.logbook?.name || ''}
                              showSearch
                              placeholder="Select a logbook"
                              optionFilterProp="children"
                              onSearch={onSearchDebouncedLogBook}
                              onChange={(value, option) => {
                                input.onChange(value);
                              }}
                              filterOption={(input, option: any) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                              }
                              options={assignLogBook?.data?.data?.map((option: any) => ({
                                value: option.id,
                                label: option.name,
                              }))}
                            />
                          )}
                        />
                      </div>
                    </Col>
                  )}
                  {/*{data.role === "dispatcher" && (*/}
                  {/*    <Col span={6}>*/}
                  {/*        <div>*/}
                  {/*            <label>LogBook: </label>*/}
                  {/*            <Field*/}
                  {/*                name="logbook"*/}
                  {/*                render={({input}: { input: any }) => (*/}
                  {/*                    <Input*/}
                  {/*                        disabled={true}*/}
                  {/*                        placeholder="Second Name"*/}
                  {/*                        type="text"*/}
                  {/*                        {...input}*/}
                  {/*                    />*/}
                  {/*                )}*/}
                  {/*            />*/}
                  {/*        </div>*/}
                  {/*    </Col>*/}
                  {/*)}*/}
                </Row>
                {/*<div>*/}
                {/*    <label>Driver: </label>*/}
                {/*    <Field*/}
                {/*        name="driverId"*/}
                {/*        render={({input}: { input: any }) => (*/}
                {/*            <Input placeholder="Second Name" type="text" {...input} />*/}
                {/*        )}*/}
                {/*    />*/}
                {/*</div>*/}
                <Row gutter={[16, 10]}>
                  <Col xs={24} sm={24} md={12} lg={6}>
                    <label>Notes: </label>
                    <Field
                      name="notes"
                      render={({ input }: { input: any }) => (
                        <Input placeholder="Notes" type="text" {...input} />
                      )}
                    />
                  </Col>

                  {data?.role === 'driver' && (
                    <Col xs={24} sm={24} md={12} lg={4}>
                      <label>Activated: </label>
                      <br />
                      <Field
                        name="activated"
                        render={({ input }: { input: any }) => {
                          return (
                            <DatePicker
                              defaultValue={defaultValue}
                              format={'YYYY-MM-DD'}
                              disabledDate={(current) => {
                                let customDate = moment().add(1, 'd').format('YYYY-MM-DD');
                                return current && current > moment(customDate, 'YYYY-MM-DD');
                              }}
                              onChange={(date: any, dateString: any) => {
                                input.onChange(
                                  moment
                                    .utc(dateString, 'YYYY-MM-DD')
                                    .add(11, 'hour')
                                    .toISOString(),
                                );
                              }}
                            />
                          );
                        }}
                      />
                    </Col>
                  )}
                  {data?.role === 'driver' && !isWhiteLabel && (
                    <Col xs={24} sm={24} md={12} lg={4}>
                      <label>Dot Date: </label>
                      <br />
                      <Field
                        name="dot_date"
                        render={({ input }: { input: any }) => {
                          return (
                            <DatePicker
                              allowClear={false}
                              defaultValue={data?.dot_date ? dayjs.utc(data.dot_date) : null}
                              format={'YYYY-MM-DD'}
                              disabledDate={(current) => {
                                let customDate = moment().add(1, 'd').format('YYYY-MM-DD');
                                return current && current > moment(customDate, 'YYYY-MM-DD');
                              }}
                              onChange={(date: any, dateString: any) => {
                                if (!date) return;
                                input.onChange(
                                  moment
                                    .utc(dateString, 'YYYY-MM-DD')
                                    .add(11, 'hour')
                                    .toISOString(),
                                );
                              }}
                            />
                          );
                        }}
                      />
                    </Col>
                  )}

                  {data.role == 'dispatcher' && (
                    <Col xs={24} sm={24} md={12} lg={3}>
                      <Field
                        name="registerStatus"
                        render={({ input, meta }: { input: any; meta: any }) => (
                          <Col>
                            <label style={{}}>Register Status: </label>

                            <Select
                              defaultValue={input.value}
                              onChange={(value, option) => {
                                input.onChange(value);
                              }}
                              style={{ width: '100%' }}
                            >
                              {roleList.map((u: any) => (
                                <Option key={u.value} value={u.value}>
                                  {u.label}
                                </Option>
                              ))}
                            </Select>
                            {meta.touched && meta.error && (
                              <div style={{ color: 'red' }}>{meta.error}</div>
                            )}
                          </Col>
                        )}
                      />
                    </Col>
                  )}
                </Row>
                {data?.role === 'driver' && user?.role !== 'sales' && (
                  <Row gutter={[16, 10]}>
                    {!isWhiteLabel && (
                      <Col xs={24} sm={24} md={12} lg={12}>
                        <Card style={{ padding: 8 }} bodyStyle={{ padding: 0 }}>
                          <Title style={{ marginBottom: 8, fontSize: 14 }}>
                            External data sharing
                          </Title>

                          <Text type="secondary">
                            Controls whether this vehicle&apos;s data can be shared with connected
                            external services. <br /> If you allow data sharing, you cannot change
                            this setting later.
                          </Text>

                          <div style={{ marginTop: 8 }}>
                            <Field
                              name="settings.disable_send_integration"
                              render={({ input }: { input: any }) => {
                                const isDisabled =
                                  data?.isLimitedAdmin === true &&
                                  (user?.role === 'support' || user?.role === 'developer');

                                return (
                                  <Radio.Group
                                    disabled={isDisabled}
                                    value={!input.value}
                                    onChange={(e) => input.onChange(!e.target.value)}
                                  >
                                    <Radio value={true} style={{ marginRight: 24 }}>
                                      Allow Sharing
                                    </Radio>
                                    <Radio value={false}>Do Not Allow</Radio>
                                  </Radio.Group>
                                );
                              }}
                            />
                          </div>
                        </Card>
                      </Col>
                    )}
                  </Row>
                )}
                <Row gutter={[16, 10]}>
                  <Col xs={24} sm={24} md={6} lg={4}>
                    <div style={{ marginTop: 25 }}>
                      <label>Is Active: </label>
                      <Field
                        name="is_active"
                        render={({ input }: { input: any }) => (
                          <Switch defaultChecked={input.value} onChange={input.onChange} />
                        )}
                      />
                    </div>
                  </Col>
                  {!isWhiteLabel && (
                    <>
                      {data.role !== 'driver' && (
                        <Col xs={24} sm={24} md={6} lg={4}>
                          <div style={{ marginTop: 25 }}>
                            <label>Is Owner: </label>
                            <Field
                              name="isOwner"
                              render={({ input }: { input: any }) => (
                                <Switch defaultChecked={input.value} onChange={input.onChange} />
                              )}
                            />
                          </div>
                        </Col>
                      )}
                      {data.role !== 'driver' && user?.role === 'manager' && (
                        <Col xs={24} sm={24} md={6} lg={4}>
                          <div style={{ marginTop: 25 }}>
                            <label>Is Support: </label>
                            <Field
                              name="isSupport"
                              render={({ input }: { input: any }) => (
                                <Switch defaultChecked={input.value} onChange={input.onChange} />
                              )}
                            />
                          </div>
                        </Col>
                      )}
                      {data.role !== 'driver' && user?.role === 'manager' && (
                        <Col xs={24} sm={24} md={6} lg={4}>
                          <div style={{ marginTop: 25 }}>
                            <label>Is Senior Support: </label>
                            <Field
                              name="config.is_full_accessed_support"
                              render={({ input }: { input: any }) => (
                                <Switch checked={!!input.value} onChange={input.onChange} />
                              )}
                            />
                          </div>
                        </Col>
                      )}
                      {data.role !== 'driver' && user?.role !== 'sales' && (
                        <Col xs={24} sm={24} md={6} lg={4}>
                          <div style={{ marginTop: 25 }}>
                            <label>Is Bot: </label>
                            <Field
                              name="isBot"
                              render={({ input }: { input: any }) => (
                                <Switch checked={!!input.value} onChange={input.onChange} />
                              )}
                            />
                          </div>
                        </Col>
                      )}
                      <Col xs={24} sm={24} md={6} lg={4}>
                        <div style={{ marginTop: 25 }}>
                          <label>Email Verified: </label>
                          <Field
                            name="emailVerified"
                            render={({ input }: { input: any }) => (
                              <Switch defaultChecked={input.value} onChange={input.onChange} />
                            )}
                          />
                        </div>
                      </Col>
                    </>
                  )}
                </Row>
                {data.lastDevice && (
                  <Row style={{ marginTop: '20px' }} gutter={[16, 10]}>
                    {data.lastDevice?.app_version && (
                      <Col xs={24} sm={24} md={6} lg={4}>
                        <div>
                          <label>App version:</label>
                          <br />
                          <Field
                            name="lastDevice.app_version"
                            render={({ input }: { input: any }) => (
                              <div style={{ fontSize: '16px', textTransform: 'uppercase' }}>
                                <Badge status="default" /> <span>{input.value}</span>
                              </div>
                            )}
                          />
                        </div>
                      </Col>
                    )}
                    {data.lastDevice?.android_version && (
                      <Col xs={24} sm={24} md={6} lg={4}>
                        <div>
                          <label>Android version:</label>
                          <br />
                          <Field
                            name="lastDevice.android_version"
                            render={({ input }: { input: any }) => (
                              <div style={{ fontSize: '16px', textTransform: 'uppercase' }}>
                                <Badge status="default" /> <span>{input.value}</span>
                              </div>
                            )}
                          />
                        </div>
                      </Col>
                    )}
                    {data.lastDevice?.device_type && (
                      <Col xs={24} sm={24} md={6} lg={4}>
                        <div>
                          <label>Device type:</label>
                          <br />
                          <Field
                            name="lastDevice.device_type"
                            render={({ input }: { input: any }) => (
                              <div style={{ fontSize: '16px', textTransform: 'uppercase' }}>
                                <Badge status="default" /> <span>{input.value}</span>
                              </div>
                            )}
                          />
                        </div>
                      </Col>
                    )}
                    {data.lastDevice?.model && (
                      <Col xs={24} sm={24} md={6} lg={4}>
                        <div>
                          <label>Model:</label>
                          <br />
                          <Field
                            name="lastDevice.model"
                            render={({ input }: { input: any }) => (
                              <div style={{ fontSize: '16px', textTransform: 'uppercase' }}>
                                <Badge status="default" /> <span>{input.value}</span>
                              </div>
                            )}
                          />
                        </div>
                      </Col>
                    )}
                  </Row>
                )}
                <Button
                  style={{ marginTop: 20 }}
                  type="primary"
                  htmlType="submit"
                  loading={loadings[0]}
                  onClick={handleButtonClick}
                >
                  Submit
                </Button>
              </Space>
            </div>
          </form>
        )}
      />
    </Card>
  );
};

export default MainFields;
